import { useEffect, useMemo, useState } from "react";

import { App, Table } from "antd";
import { Form, FormikProvider, FormikValues, useFormik } from "formik";
import { useAtom, useAtomValue } from "jotai";
import { useMutation, useQuery } from "@tanstack/react-query";
import { mixed, object, string } from "yup";

import { Input, Select } from "../../custom";
import Upload from "../../custom/upload/upload";
import { ReactComponent as Edit } from "../../assets/pencil-paper.svg";
import { ReactComponent as Trash } from "../../assets/trash.svg";
import {
  createUpdateJambResult,
  getJambByApplicantId,
  getSubjects,
} from "../../utils/request";
import { pageIndexAtom, userAtom } from "../../utils/store";
import OnboardingButtonGroup from "./onboardingButtonGroup";
import styles from "./styles.module.scss";

interface UtmeSubject {
  score: string;
  subject: string;
  activeStatus?: boolean;
}

interface SavedUtmeResult {
  examNumber: string;
  examYear: string;
  subjects: UtmeSubject[];
  jambResultMeta: {
    name: string;
    size: number;
  } | null;
  jambResultUrl?: string;
}

const UTME_RESULT_STORAGE_KEY = "utme-result";

const getDocumentName = (value?: string | null) => {
  if (!value) return "JambResult";

  const normalizedValue = value.split("?")[0];
  const segments = normalizedValue.split("/");
  return segments[segments.length - 1] || "JambResult";
};

const normalizeSubjects = (items: UtmeSubject[]) =>
  items
    .map((item) => ({
      subject: item.subject.trim().toLowerCase(),
      score: `${Number(item.score) || 0}`,
    }))
    .sort((a, b) => a.subject.localeCompare(b.subject));

const mapJambSubjectsToUtmeSubjects = (items: JambResultSubject[] = []) =>
  items.map((item) => ({
    score: `${item.score ?? ""}`,
    subject: item.subjectName ?? "",
    activeStatus: true,
  }));

const UtmeResult = () => {
  const { notification } = App.useApp();
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const user = useAtomValue(userAtom);

  const savedUtmeResult = (() => {
    try {
      return JSON.parse(
        localStorage.getItem(UTME_RESULT_STORAGE_KEY) ?? "null",
      ) as SavedUtmeResult | null;
    } catch (_error) {
      return null;
    }
  })();

  const [subjects, setSubjects] = useState<UtmeSubject[]>(
    savedUtmeResult?.subjects ?? [],
  );
  const [subjectToEdit, setSubjectToEdit] = useState<UtmeSubject>({
    score: "",
    subject: "",
  });
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [jambResultMeta, setJambResultMeta] = useState<{
    name: string;
    size: number;
  } | null>(savedUtmeResult?.jambResultMeta ?? null);
  const [existingJambResultUrl, setExistingJambResultUrl] = useState(
    savedUtmeResult?.jambResultUrl ?? "",
  );

  const createUpdateJambResultMutation = useMutation({
    mutationFn: createUpdateJambResult,
  });

  const { data: jambResultResponse } = useQuery({
    queryKey: ["get-jamb-result", user?.applicantId],
    queryFn: () => getJambByApplicantId(user?.applicantId ?? ""),
    enabled: !!user?.applicantId,
  });

  const jambResultData = useMemo(
    () => jambResultResponse?.data,
    [jambResultResponse?.data],
  );

  const hasExistingServerJambResult = !!(
    jambResultData?.examNumber &&
    (jambResultData?.jambResultUrl || jambResultData?.jambResult)
  );

  const isExistingServerRecordUnchanged = (values: FormikValues) => {
    const existingSubjects = normalizeSubjects(
      mapJambSubjectsToUtmeSubjects(jambResultData?.subjects ?? []),
    );
    const currentSubjects = normalizeSubjects(subjects);

    return (
      hasExistingServerJambResult &&
      !values.jambResult &&
      (jambResultData?.examNumber ?? "") === values.examNumber &&
      (jambResultData?.examYear ?? "") === values.examYear &&
      JSON.stringify(existingSubjects) === JSON.stringify(currentSubjects)
    );
  };

  const validationRules = object().shape({
    examNumber: string().required("Exam number is required"),
    examYear: string()
      .required("Exam year is required")
      .matches(/^\d{4}$/, "Exam year must be a 4-digit year"),
    subject: string().when([], {
      is: () => subjects.length === 0 && !isEditingSubject,
      then: (schema) => schema.required("Subject is required"),
      otherwise: (schema) => schema,
    }),
    score: string().when([], {
      is: () => subjects.length === 0 && !isEditingSubject,
      then: (schema) =>
        schema
          .required("Score is required")
          .test("validScore", "Score must be between 0 and 100", (value) => {
            if (!value) return false;
            const score = Number(value);
            return !Number.isNaN(score) && score >= 0 && score <= 100;
          }),
      otherwise: (schema) => schema,
    }),
    jambResult: mixed()
      .test(
        "fileRequired",
        "JAMB result is required",
        (value) => !!value || !!jambResultMeta || !!existingJambResultUrl,
      )
      .test("fileFormat", "*Only image or pdf files are allowed", (value) => {
        if (value) {
          const supportedFormats = ["image", "application/pdf"];
          return supportedFormats.includes((value as File)?.type);
        }
        return true;
      })
      .test("fileSize", "File size must be less than 3MB", (value) => {
        if (value) {
          return (value as File)?.size <= 3145728;
        }
        return true;
      }),
    subjects: mixed().test(
      "exactFourSubjects",
      "Exactly four subjects are required",
      () => subjects.length === 4,
    ),
  });

  const { data: subjectData } = useQuery({
    queryKey: ["get-all-subject"],
    queryFn: getSubjects,
  });

  const subjectOptions = subjectData?.data?.map((item) => (
    <option key={item?.id} value={item?.subject}>
      {item?.subject}
    </option>
  ));

  const formik = useFormik<FormikValues>({
    initialValues: {
      examNumber:
        jambResultData?.examNumber ?? savedUtmeResult?.examNumber ?? "",
      examYear: jambResultData?.examYear ?? savedUtmeResult?.examYear ?? "",
      subjects:
        jambResultData?.subjects?.map((item) => ({
          score: `${item.score ?? ""}`,
          subject: item.subjectName ?? "",
          activeStatus: true,
        })) ??
        savedUtmeResult?.subjects ??
        [],
      jambResult: null,
      subject: "",
      score: "",
    },
    enableReinitialize: true,
    validationSchema: validationRules,
    onSubmit: async (values) => {
      const saveDraftAndContinue = (message?: string) => {
        const savedPayload: SavedUtmeResult = {
          examNumber: values.examNumber,
          examYear: values.examYear,
          subjects,
          jambResultMeta:
            jambResultMeta ??
            (existingJambResultUrl
              ? {
                  name: getDocumentName(existingJambResultUrl),
                  size: 0,
                }
              : null),
          jambResultUrl: existingJambResultUrl,
        };

        localStorage.setItem(
          UTME_RESULT_STORAGE_KEY,
          JSON.stringify(savedPayload),
        );

        if (message) {
          notification.success({
            message: "Success",
            description: message,
          });
        }

        setPageIndex(pageIndex + 1);
      };

      if (isExistingServerRecordUnchanged(values)) {
        saveDraftAndContinue("UTME result already on file");
        return;
      }

      const payload = new FormData();
      const totalScore = subjects.reduce(
        (sum, item) => sum + (Number(item.score) || 0),
        0,
      );

      payload.append("ApplicantId", user?.applicantId ?? "");
      payload.append("ExamNumber", values.examNumber);
      payload.append("ExamYear", values.examYear);
      payload.append("TotalScore", `${totalScore}`);
      payload.append("InstitutionShortName", user?.institutionShortName ?? "");

      if (values.jambResult) {
        payload.append("JambResult", values.jambResult);
      } else if (existingJambResultUrl) {
        try {
          const response = await fetch(existingJambResultUrl);
          const blob = await response.blob();

          payload.append(
            "JambResult",
            blob,
            getDocumentName(existingJambResultUrl),
          );
        } catch (_error) {
          notification.error({
            message: "Error",
            description: "Please re-upload your JAMB result to continue",
          });
          return;
        }
      }

      subjects.forEach((item, index) => {
        payload.append(`Subjects[${index}].subjectName`, item.subject);
        payload.append(
          `Subjects[${index}].score`,
          `${Number(item.score) || 0}`,
        );
        payload.append(`Subjects[${index}].activeStatus`, `${item.activeStatus ?? true}`);
      });

      try {
        await createUpdateJambResultMutation.mutateAsync(payload, {
          onSuccess: (data) => {
            saveDraftAndContinue(
              data?.message || "UTME result saved successfully",
            );
          },
        });
      } catch (error: any) {
        notification.error({
          message: "Error",
          description:
            error?.response?.data?.message || "Failed to save UTME result",
        });
      }
    },
  });

  const columns = [
    {
      title: "#",
      dataIndex: "index",
      render: (_: unknown, _record: UtmeSubject, index: number) => index + 1,
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_: string, record: UtmeSubject) => (
        <div className="small-gap">
          <button
            type="button"
            onClick={() => {
              setIsEditingSubject(true);
              setSubjectToEdit(record);
              formik.setFieldValue("score", record?.score);
              formik.setFieldValue("subject", record?.subject);
            }}
            className={styles.transparentBtn}
          >
            <Edit />
          </button>
          <button
            type="button"
            onClick={() =>
              setSubjects(
                subjects.filter(
                  (item) =>
                    item.score !== record?.score ||
                    item.subject !== record?.subject,
                ),
              )
            }
            className={styles.transparentBtn}
          >
            <Trash />
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!jambResultData?.examNumber) {
      return;
    }

    const mappedSubjects = mapJambSubjectsToUtmeSubjects(
      jambResultData.subjects ?? [],
    );

    setSubjects(mappedSubjects);

    const fileUrl = jambResultData.jambResultUrl ?? jambResultData.jambResult ?? "";
    setExistingJambResultUrl(fileUrl);

    if (fileUrl) {
      setJambResultMeta((currentMeta) => ({
        name: currentMeta?.name || getDocumentName(fileUrl),
        size: currentMeta?.size || 0,
      }));
    }
  }, [jambResultData]);

  const handleAddSubject = () => {
    const hasValue = !!formik.values.score && !!formik.values.subject;
    const alreadyExists = subjects.some(
      (item) => item.subject === formik.values.subject,
    );

    if (!hasValue) {
      notification.error({
        message: "Error",
        description: "Please enter a score and select a subject",
      });
      return;
    }

    if (!isEditingSubject && subjects.length >= 4) {
      notification.error({
        message: "Error",
        description: "You can only add exactly four subjects",
      });
      return;
    }

    if (alreadyExists && !isEditingSubject) {
      notification.error({
        message: "Subject already exists",
      });
      return;
    }

    if (isEditingSubject) {
      const index = subjects.findIndex(
        (item) =>
          item.subject === subjectToEdit.subject &&
          item.score === subjectToEdit.score,
      );

      if (index !== -1) {
        const updatedSubjects = [...subjects];
        updatedSubjects[index] = {
          score: formik.values.score,
          subject: formik.values.subject,
        };
        setSubjects(updatedSubjects);
      }

      setIsEditingSubject(false);
    } else {
      setSubjects([
        ...subjects,
        {
          score: formik.values.score,
          subject: formik.values.subject,
        },
      ]);
    }

    formik.setFieldValue("score", "");
    formik.setFieldValue("subject", "");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      setExistingJambResultUrl("");
      setJambResultMeta({
        name: file.name,
        size: file.size,
      });
      formik.setFieldValue("jambResult", file);
    }
  };

  const handlePrevClick = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const handleNextClick = async () => {
    if (subjects.length !== 4) {
      notification.error({
        message: "Error",
        description: "Please add exactly four subjects to proceed",
      });
      return;
    }

    await formik.setFieldValue("subjects", subjects);

    if (isExistingServerRecordUnchanged(formik.values)) {
      const savedPayload: SavedUtmeResult = {
        examNumber: formik.values.examNumber,
        examYear: formik.values.examYear,
        subjects,
        jambResultMeta:
          jambResultMeta ??
          (existingJambResultUrl
            ? {
                name: getDocumentName(existingJambResultUrl),
                size: 0,
              }
            : null),
        jambResultUrl: existingJambResultUrl,
      };

      localStorage.setItem(
        UTME_RESULT_STORAGE_KEY,
        JSON.stringify(savedPayload),
      );
      formik.setFieldError("jambResult", undefined);
      formik.setFieldTouched("jambResult", false, false);
      setPageIndex(pageIndex + 1);
      return;
    }

    formik.submitForm();
  };

  return (
    <FormikProvider value={formik}>
      <Form>
        <section className={styles.section}>
          <h2>UTME Result</h2>
          <br />
          <p>Input your UTME details and subject scores</p>
        </section>
        <hr />

        <div className={styles.section}>
          <div className={styles.formWrapper}>
            <Input
              name="examNumber"
              label="Exam Number"
              placeholder="Enter exam number"
            />
            <Input
              name="examYear"
              label="Exam Year"
              placeholder="Enter exam year"
              type="number"
            />

            <section className={styles.grid}>
              <Select
                name="subject"
                label="Subject"
                placeholder="Select subject"
                options={subjectOptions}
                onChange={formik.handleChange}
              />
              <Input
                name="score"
                label="Score"
                placeholder="Enter score"
                type="number"
              />
            </section>
          </div>

          <button
            type="button"
            onClick={handleAddSubject}
            className={styles.addSubject}
          >
            <span>+</span>
            <span>{isEditingSubject ? "Save" : "Add"} Subject</span>
          </button>

          {subjects.length > 0 && (
            <Table
              dataSource={subjects}
              columns={columns}
              pagination={false}
              rowKey={(record) => `${record.subject}-${record.score}`}
            />
          )}

          <Upload
            fileName={jambResultMeta?.name}
            fileSize={jambResultMeta?.size}
            onChange={handleFileChange}
            name="jambResult"
            label="Upload JAMB Result"
          />

          <OnboardingButtonGroup
            onClick={handlePrevClick}
            onNextClick={handleNextClick}
            isLoading={createUpdateJambResultMutation.isPending}
          />
        </div>
      </Form>
    </FormikProvider>
  );
};

export default UtmeResult;
